/**
 * অবস্থা ৩ · ইন্টারনেট নেই — `P0 States § ৬` (Failure · Offline). `E_OFFLINE`.
 *
 * Frame note: "Offline is a reduced app, not a blocked one: live orders, receipts and
 * saved numbers still open. The saved technician's number matters most — offline is
 * exactly when someone needs to call."
 *
 * The second universal row: `হ্যাঁ` on all seven surfaces.
 *
 * W1-04 · WHAT THIS FRAME NO LONGER SAYS, AND WHY (`docs/deviations/W1-04.md`, S6):
 *  · the drawn name. The greeting now carries the person's own name, as this device last
 *    heard it (`auth/session.ts § cachedDisplayName`), or stands alone.
 *  · the "অফলাইনেও যা দেখা যাবে" list and the banner line that points at it. The frame
 *    note describes an app that keeps orders, receipts and numbers on the phone; this one
 *    does not — `src/sw.ts` never stores an API answer — so all three rows opened onto
 *    this same frame again, and their counts and times were the drawn afternoon's. When
 *    the app can hold them offline, the list comes back with real rows.
 * What it still says is all true: there is no connection, new orders need one, and the
 * retry button retries.
 *
 * TWO OFF-PALETTE COLOURS, TRANSCRIBED AND FLAGGED. The banner is
 * `background:#FDF6E8; border-bottom:1px solid #B07A16`. Neither is in docs/02, and
 * both recur across § ৬, § ৭ and § ৯. See chrome.ts § WARNING_PANEL — token-lint
 * flags them and the flag is correct.
 *
 * DEVIATION, reported: `@oprappo/ui`'s `FailureState.Offline` draws § 07's offline
 * card — a bordered sand box with a two-colour wave-and-slash glyph. § ৬ draws a
 * full-width banner in the warning pair with a one-colour glyph and different arcs.
 * The frame is transcribed; the component is not forked.
 */
import { StateFrame, WARNING_PANEL, OfflineGlyph, RetryGlyph } from './chrome.js';
import { OFFLINE } from './data.js';

export interface OfflineStateProps {
  /** Retry is the one way out this frame draws. */
  onRetry?: (() => void) | undefined;
  /**
   * The person's own name, or nothing. Never the name the frame was drawn for: that
   * greeted every customer who lost signal as the same stranger.
   */
  nameBn?: string | null | undefined;
}

export function OfflineState({ onRetry, nameBn }: OfflineStateProps = {}): JSX.Element {
  const d = OFFLINE;
  const name = nameBn === null || nameBn === undefined || nameBn.trim() === '' ? null : nameBn;
  return (
    <StateFrame stateKey="offline">
      <div className="bg-teal px-[16px] pt-[16px] pb-[18px]">
        <div className="flex justify-between items-center mb-[14px]">
          <div>
            <div className="font-anek font-normal text-[13px] leading-[1.75] text-on-teal-muted">
              {d.greeting_bn}
            </div>
            {name === null ? null : (
              <div className="font-anek font-semibold text-[17px] leading-[1.75] text-white mt-[1px]">
                {name}
              </div>
            )}
          </div>
        </div>
        {/* The search field is not hidden, it is disabled and says why. */}
        <div className="bg-teal-dark box-content border border-white/20 h-[50px] flex items-center px-[13px] gap-[11px]">
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
            <circle cx="11" cy="11" r="7" stroke="#8FB0AE" strokeWidth={2} />
            <path d="M16 16l4 4" stroke="#8FB0AE" strokeWidth={2} />
          </svg>
          <div className="flex-1 font-anek font-normal text-[16px] leading-[1.75] text-on-teal-muted">
            {d.search_disabled_bn}
          </div>
        </div>
      </div>

      <div role="status" className={WARNING_PANEL + ' px-[16px] py-[13px] flex gap-[12px] items-start'}>
        <OfflineGlyph />
        <div className="flex-1">
          <div className="font-anek font-semibold text-[15.5px] leading-[1.75] text-ink">
            {d.banner_title_bn}
          </div>
        </div>
      </div>

      <div className="flex-1 p-[16px] oprappo-scroll">
        {/* What is NOT possible is stated once, in a dashed placeholder box, rather
            than left for the user to discover by tapping. */}
        <div className="border border-dashed border-border-warm bg-sand-faint p-[12px]">
          <div className="font-anek font-normal text-[14px] leading-[1.75] text-body">{d.blocked_bn}</div>
        </div>
      </div>

      <div className="bg-white border-t border-border px-[16px] py-[12px]">
        <button
          type="button"
          onClick={onRetry}
          className="box-content border-[1.5px] border-teal h-[48px] p-0 flex items-center justify-center gap-[9px] font-anek font-semibold text-[15px] leading-[1.75] text-teal bg-transparent rounded-none"
        >
          <RetryGlyph />
          {d.retry_bn}
        </button>
      </div>
    </StateFrame>
  );
}
